import React, { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { Card } from 'components/common/card'
import { Logo } from 'components/common/logo'
import { Column, SpaceBetween } from 'components/common/layout'
import { BoldText, SemiBoldText, SmallText } from 'components/common/text'
import { FiChevronRight } from 'react-icons/fi'
import { useStores } from 'stores/globals'
import { textEllipsis } from 'components/common/styles'
import { observer } from 'mobx-react'
import { useRouter } from 'next/router'
import { analytics } from 'infra/analytics'
import { StartupPositionForAnalysisParams } from 'stores/position-analysis'
import { useIsDesktop } from 'utils/hooks'
import { useIsClient } from 'usehooks-ts'

export const PositionAnalysisPushAlarm: React.FC = observer(() => {
  const { positionAnalysisStore } = useStores()
  const isDesktop = useIsDesktop()
  const isLoaded = useRef(false)
  const isClient = useIsClient()
  const analysisKeys = Object.keys(positionAnalysisStore.analysisList)

  useEffect(() => {
    if (isLoaded.current || !isClient) return
    analysisKeys.forEach((key) => {
      const analysis = positionAnalysisStore.getAnalysisItem(key)
      if (analysis && analysis.isLoading) {
        positionAnalysisStore.addAnalysisInterval(analysis)
      }
    })
    isLoaded.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClient, isLoaded])

  if (!isDesktop) return <MobilePopupPushAlarm />

  return null
})

const MobilePopupPushAlarm: React.FC = observer(() => {
  const router = useRouter()
  const { positionAnalysisStore } = useStores()
  const [isExtended, setIsExtended] = useState(false)
  const [closingKey, setClosingKey] = useState<number | null>(null)

  if (positionAnalysisStore.sortedAnalysisList.length === 0) return null

  const handleClose = (id: number) => {
    analytics.track('click_push_alarm_item', { positionId: id })
    setClosingKey(id)
    setTimeout(() => {
      positionAnalysisStore.removeAnalysis(id)
      setClosingKey(null)
      setIsExtended(false)
      router.push(`/positions/${id}/analysis`)
    }, 300) // transition-duration과 맞춰야 함
  }

  return (
    <PopupContainer isExtend={isExtended}>
      <ScrollContainer isExtend={isExtended}>
        {isExtended ? (
          positionAnalysisStore.sortedAnalysisList.map((analysis) => (
            <AlarmItem
              key={analysis.id}
              item={analysis}
              onClick={() => handleClose(analysis.id)}
              className={closingKey === analysis.id ? 'slide-out' : ''}
            />
          ))
        ) : (
          <div style={{ position: 'relative' }}>
            {positionAnalysisStore.sortedAnalysisList.length > 2 && (
              <BackgroundCard style={{ bottom: -8, width: '94%' }} />
            )}
            {positionAnalysisStore.sortedAnalysisList.length > 1 && (
              <BackgroundCard style={{ bottom: 0, width: '97%' }} />
            )}
            <div
              style={{
                width: '100%',
                position: 'absolute',
                bottom:
                  positionAnalysisStore.sortedAnalysisList.length > 1 ? 8 : 0,
                left: 0,
              }}
            >
              <AlarmItem
                key={positionAnalysisStore.sortedAnalysisList[0].id}
                className={
                  closingKey === positionAnalysisStore.sortedAnalysisList[0].id
                    ? 'slide-out'
                    : ''
                }
                item={positionAnalysisStore.sortedAnalysisList[0]}
                onClick={() => {
                  if (positionAnalysisStore.sortedAnalysisList.length > 1) {
                    setIsExtended(true)
                  } else {
                    handleClose(positionAnalysisStore.sortedAnalysisList[0].id)
                  }
                }}
              />
            </div>
          </div>
        )}
      </ScrollContainer>
      {isExtended && (
        <CloseText onClick={() => setIsExtended(false)}>닫기</CloseText>
      )}
    </PopupContainer>
  )
})

const AlarmItem: React.FC<{
  item: StartupPositionForAnalysisParams
  onClick: () => void
  className?: string
}> = ({ item, onClick, className }) => (
  <AlarmCard onClick={onClick} className={className}>
    <Logo
      size={44}
      src={item.startup.thumbnail}
      placeholder={item.startup.name}
    />
    <TextWrapper>
      <SpaceBetween>
        <MainText>서류 합격 가능성 분석이 끝났어요</MainText>
        <FiChevronRight size={18} />
      </SpaceBetween>
      <SubText>
        [{item.startup.name}] {item.name}
      </SubText>
    </TextWrapper>
  </AlarmCard>
)

const ScrollContainer = styled(Column)<{ isExtend: boolean }>`
  gap: 8px;
  padding: 16px;
  ${(p) =>
    p.isExtend &&
    css`
      justify-content: start;
      overflow: hidden scroll;
      max-height: calc(100vh - 40px);
    `}
`

const PopupContainer = styled(Column)<{ isExtend: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 999;
  width: 100%;
  ${(p) =>
    p.isExtend &&
    css`
      width: 100%;
      height: 100%;
      justify-content: start;
      background-color: ${p.theme.color.black2}88;
    `}
`

const AlarmCard = styled(Card)`
  gap: 8px;
  width: 100%;
  border-radius: 8px;
  flex-direction: row;
  justify-content: start;
  padding: 12px 10px 12px 12px;
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: transform 0.3s ease, opacity 0.3s ease;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeInSlideUp 0.4s forwards;
  &.slide-out {
    transform: translateX(100%);
    opacity: 0;
  }
  @keyframes fadeInSlideUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const MainText = styled(SemiBoldText)`
  font-size: 14px;
`

const SubText = styled(SmallText)`
  ${textEllipsis}
`

const TextWrapper = styled(Column)`
  gap: 4px;
  width: 100%;
  overflow: hidden;
`

const CloseText = styled(BoldText)`
  padding: 16px 24px 16px;
  text-align: end;
  color: ${(p) => p.theme.color.white1};
`

const BackgroundCard = styled(Card)`
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  width: 100%;
  height: 64px;
  border-radius: 8px;
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.08);
`
